import { DocumentType } from "../constants/enums";

class Validator {
    constructor(locale) {
        this.locale = locale;
    }

    get givenName() {
        return {
            required: this.locale.givenName.required,
            maxLength: {
                value: 60,
                message: this.locale.givenName.maxLength
            },
            validate: value =>
                value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
                this.locale.givenName.pattern
        };
    }

    get familyName() {
        return {
            required: this.locale.familyName.required,
            maxLength: {
                value: 100,
                message: this.locale.familyName.maxLength
            },
            validate: value =>
                value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
                this.locale.familyName.pattern
        };
    }

    get email() {
        return {
            required: this.locale.email.required,
            pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: this.locale.email.pattern,
            }
        };
    }

    get phoneNumber() {
        return {
            required: this.locale.phoneNumber.required,
        };
    }

    get treatment() {
        return {
            maxLength: {
                value: 50,
                message: this.locale.treatment.maxLength
            },
            validate: value =>
              value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
              this.locale.treatment.pattern
        };
    }

    get profession() {
        return {
            maxLength: {
                value: 50,
                message: this.locale.profession.maxLength
            },
            validate: value =>
                value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
                this.locale.profession.pattern
        };
    }

    get jobTitle() {
        return {
            maxLength: {
                value: 50,
                message: this.locale.jobTitle.maxLength
            },
            validate: value =>
                value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
                this.locale.jobTitle.pattern
        };
    }

    get jobArea() {
        return {
            validate: value =>
              value.match(new RegExp(/^[A-Za-zÀ-ÖØ-öø-ÿ ]*$/g)) ||
              this.locale.jobArea.pattern
        };
    }

    get username() {
        return {
            required: this.locale.username.required,
            validate: value =>
                value.match(new RegExp(/^[\w.\-_]*$/g)) ||
                this.locale.username.pattern
        };
    }

    get password() {
        return {
            required: this.locale.password.required,
            minLength: {
                value: 8,
                message: this.locale.password.minLength
            },
            validate: {
                alphanumeric: value =>
                    value.match(new RegExp(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).+$/g)) ||
                    this.locale.password.alphanumeric,
                specialCharacters: value =>
                    value.match(new RegExp(/^(?=.*[!@#$%^&*]).+$/g)) ||
                    this.locale.password.specialCharacters
            }
        };
    }

    get confirmPassword() {
        return (password) => ({
            required: this.locale.confirmPassword.required,
            validate: value =>
                value === password || this.locale.confirmPassword.match
        });
    }

    get identityDocument() {
        return (documentType) => ({
            required: this.locale.identityDocument.required,
            validate: {
                startsWith: (value) => {
                    if (parseInt(documentType, 10) !== DocumentType.RUC) {
                        return true;
                    }

                    if (value.length < 2) {
                        return true;
                    }

                    return value.match(new RegExp(/^(10|20)/)) || this.locale.ruc.startsWith;
                },
                numeric: (value) => {
                    const regex = new RegExp(/^[0-9]*$/);

                    switch (parseInt(documentType, 10)) {
                        case DocumentType.DNI:
                            return value.match(regex) || this.locale.dni.numeric;
                        case DocumentType.RUC:
                            return value.match(regex) || this.locale.ruc.numeric;
                        default:
                            return true;
                    }
                },
                alphanumeric: (value) => {
                    const regex = new RegExp(/^[a-zA-Z0-9]*$/g);

                    switch (parseInt(documentType, 10)) {
                        case DocumentType.CARNET_EXTRANJERIA:
                            return value.match(regex) || this.locale.carnetExtranjeria.alphanumeric;
                        case DocumentType.PASAPORTE:
                            return value.match(regex) || this.locale.passport.alphanumeric;
                        case DocumentType.PARTIDA_NACIMIENTO:
                            return value.match(regex) || this.locale.birthCertificate.alphanumeric;
                        case DocumentType.OTRO:
                            return value.match(regex) || this.locale.otherDocument.alphanumeric;
                        default:
                            return true;
                    }
                },
                length: (value) => {
                    switch (parseInt(documentType, 10)) {
                        case DocumentType.DNI:
                            return value.length === 8 || this.locale.dni.length;
                        case DocumentType.CARNET_EXTRANJERIA:
                            return value.length <= 12 || this.locale.carnetExtranjeria.length;
                        case DocumentType.RUC:
                            return value.length === 11 || this.locale.ruc.length;
                        case DocumentType.PASAPORTE:
                            return value.length <= 12 || this.locale.passport.length;
                        case DocumentType.PARTIDA_NACIMIENTO:
                            return value.length <= 15 || this.locale.birthCertificate.length;
                        case DocumentType.OTRO:
                            return value.length <= 15 || this.locale.otherDocument.length;
                        default:
                            return true;
                    }
                },
            }
        });
    }

    get notRequiredIdentityDocument() {
        return (documentType) => ({
            validate: {
                startsWith: (value) => {
                    if (parseInt(documentType, 10) !== DocumentType.RUC) {
                        return true;
                    }

                    if (value.length < 2) {
                        return true;
                    }

                    return value.match(new RegExp(/^(10|20)/)) || this.locale.ruc.startsWith;
                },
                numeric: (value) => {
                    const regex = new RegExp(/^[0-9]*$/);

                    switch (parseInt(documentType, 10)) {
                        case DocumentType.DNI:
                            return value.match(regex) || this.locale.dni.numeric;
                        case DocumentType.RUC:
                            return value.match(regex) || this.locale.ruc.numeric;
                        default:
                            return true;
                    }
                },
                alphanumeric: (value) => {
                    const regex = new RegExp(/^[a-zA-Z0-9]*$/g);

                    switch (parseInt(documentType, 10)) {
                        case DocumentType.CARNET_EXTRANJERIA:
                            return value.match(regex) || this.locale.carnetExtranjeria.alphanumeric;
                        case DocumentType.PASAPORTE:
                            return value.match(regex) || this.locale.passport.alphanumeric;
                        case DocumentType.PARTIDA_NACIMIENTO:
                            return value.match(regex) || this.locale.birthCertificate.alphanumeric;
                        case DocumentType.OTRO:
                            return value.match(regex) || this.locale.otherDocument.alphanumeric;
                        default:
                            return true;
                    }
                },
                length: (value) => {
                    switch (parseInt(documentType, 10)) {
                        case DocumentType.DNI:
                            return value.length === 8 || this.locale.dni.length;
                        case DocumentType.CARNET_EXTRANJERIA:
                            return value.length <= 12 || this.locale.carnetExtranjeria.length;
                        case DocumentType.RUC:
                            return value.length === 11 || this.locale.ruc.length;
                        case DocumentType.PASAPORTE:
                            return value.length <= 12 || this.locale.passport.length;
                        case DocumentType.PARTIDA_NACIMIENTO:
                            return value.length <= 15 || this.locale.birthCertificate.length;
                        case DocumentType.OTRO:
                            return value.length <= 15 || this.locale.otherDocument.length;
                        default:
                            return true;
                    }
                },
            }
        });
    }

    get addressCountry() {
        return { required: this.locale.country.required };
    }

    get addressState() {
        return { required: this.locale.state.required };
    }

    get addressCity() {
        return { required: this.locale.city.required };
    }

    get address1() {
        return { required: this.locale.address1.required };
    }

    get shippingAddress() {
        return { required: this.locale.shippingAddress.required };
    }

    get paymentMethod() {
        return { required: this.locale.paymentMethod.required };
    }

    get creditCard() {
        return { required: this.locale.creditCard.required };
    }

    get subscriptionPlan() {
        return (defaultProductId) => ({
            required: this.locale.subscriptionPlan.required,
            validate: value =>
              value !== defaultProductId || this.locale.subscriptionPlan.match
        });
    }

    get couponDiscount() {
        return {
            validate: {
                alphanumeric: value =>
                  !value || new RegExp(/^[a-zA-Z0-9-]+$/g).test(value) ||
                  this.locale.couponDiscount.alphanumeric,
            }
        };
    }
}

export default Validator;
